import { useState } from "react";
import { Link } from "react-router-dom";
import { PawPrint, EnvelopeSimple, ArrowLeft, CheckCircle } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";

export default function ForgotPassword() {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-pf-bg flex flex-col p-8">
      <div className="flex items-center gap-2 mb-10">
        <div className="h-9 w-9 rounded-lg bg-pf-primary text-white grid place-items-center">
          <PawPrint size={20} weight="fill" />
        </div>
        <span className="font-display font-extrabold text-lg tracking-tight text-pf-text">Pawfile</span>
      </div>
      <div className="flex-1 flex items-start justify-center">
        <div className="pf-card p-8 w-full max-w-md">
          {sent ? (
            <div>
              <div className="h-12 w-12 rounded-full bg-pf-success-bg grid place-items-center mb-4">
                <CheckCircle size={28} weight="fill" className="text-pf-success" />
              </div>
              <h1 className="text-2xl font-display font-bold text-pf-text">Check your inbox</h1>
              <p className="mt-2 text-sm text-pf-text-secondary">
                If an account exists for <span className="font-medium text-pf-text">{email}</span>, we&apos;ve sent a password reset link. The link expires in 1 hour.
              </p>
              <Link to="/login" className="mt-6 inline-flex items-center gap-1 text-sm text-pf-primary hover:underline">
                <ArrowLeft size={14} /> Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-display font-bold text-pf-text">Forgot your password?</h1>
              <p className="mt-1 text-sm text-pf-text-secondary">Enter your email and we&apos;ll send a reset link.</p>
              <form onSubmit={onSubmit} className="mt-6 space-y-4">
                <div>
                  <label className="pf-label mb-1.5 block">{t("email")}</label>
                  <div className="relative">
                    <EnvelopeSimple size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-pf-text-secondary" />
                    <input
                      data-testid="forgot-email-input"
                      type="email"
                      required
                      autoFocus
                      className="pf-input pl-9"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                </div>
                {err && <div className="text-sm text-pf-danger bg-pf-danger-bg border border-pf-danger/20 rounded-lg px-3 py-2">{err}</div>}
                <button data-testid="forgot-submit-btn" disabled={busy} type="submit" className="pf-btn-primary w-full justify-center py-2.5">
                  {busy ? "Sending…" : "Send reset link"}
                </button>
              </form>
              <Link to="/login" className="mt-6 inline-flex items-center gap-1 text-sm text-pf-text-secondary hover:text-pf-text">
                <ArrowLeft size={14} /> Back to sign in
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
